import React, { useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { listAccounts, createAccount, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];

const AccountsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'asset', subtype: '' });

  const load = () => {
    setLoading(true);
    listAccounts().then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    try {
      await createAccount(form);
      setOpenNew(false);
      setForm({ code: '', name: '', type: 'asset', subtype: '' });
      toast({ title: 'Account created' });
      load();
    } catch (e) {
      toast({ title: errMsg(e), variant: 'destructive' });
    }
  };

  const grouped = TYPES.map((t) => ({ type: t, accounts: items.filter((a) => a.type === t) }));

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Ledger</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Chart of Accounts</h1>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid="new-account-btn"
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
          <Plus className="w-4 h-4" /> New Account
        </button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="space-y-4">
          {grouped.map(({ type, accounts }) => (
            <div key={type} className="bg-white rounded-2xl border border-black/5 overflow-hidden">
              <div className="px-4 py-2 bg-[#f7fbf8] border-b border-black/5 text-xs font-semibold uppercase tracking-widest text-[#0f5c3d]">
                {type} <span className="text-[#0b1a2e]/50 font-normal ml-2">({accounts.length})</span>
              </div>
              {accounts.length === 0 ? (
                <div className="px-4 py-4 text-xs text-[#0b1a2e]/40 italic">No accounts of this type.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {accounts.map((a) => (
                      <tr key={a.account_id} className="border-t border-slate-50 hover:bg-[#f7fbf8]">
                        <td className="px-4 py-2 font-mono text-xs text-[#0b1a2e]/70 w-24">{a.code}</td>
                        <td className="px-4 py-2 text-[#0b1a2e]">{a.name}</td>
                        <td className="px-4 py-2 text-[10px] uppercase text-[#0b1a2e]/50 w-32 tracking-widest">{a.subtype}</td>
                        <td className="px-4 py-2 text-right w-24">
                          {a.is_system && <span className="text-[10px] uppercase tracking-widest text-[#0f5c3d]/60">seeded</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-medium text-[#0b1a2e] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>New Account</h2>
            <div className="space-y-3">
              <F label="Code"><input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              <F label="Name"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              <F label="Type">
                <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </F>
              <F label="Subtype (optional)"><input value={form.subtype} onChange={(e) => setForm((f) => ({ ...f, subtype: e.target.value }))} placeholder="e.g. current_asset, bank" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpenNew(false)} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
              <button onClick={submit} disabled={!form.code || !form.name} data-testid="account-save"
                className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default AccountsPage;
